import type { Tela, TelaDeOrigem } from "./Aplicacao"
import type { AbaDaFicha, SubmenuDaFicha } from "./telas/Ficha"
import { ehAbaDoPerfil } from "./telas/Perfil"
import { buscaDoPainel, estadoDaBusca, type EstadoDoPainel } from "./estado-do-painel"
import { FILTROS_DO_ITEM, ehItemDeConfiguracao } from "./itens-de-configuracao"
import type { ChaveDeTela } from "./registro-de-telas"

/**
 * O ROTEADOR — spec 0044, 24/09/2026, pedido direto. Duas funções puras
 * (nenhuma toca `window`; quem chama decide quando ler/escrever o
 * endereço), sem biblioteca — decisão registrada em `ADR-14`.
 *
 * `/autenticidade/<código>` NÃO passa por aqui: continua lido à parte,
 * antes de qualquer `Tela`, em `Aplicacao.tsx` (`codigoDeAutenticidade`).
 *
 * SIMPLIFICAÇÃO DELIBERADA: `submenu`/`submenuDaFicha` no `Tela` de hoje
 * é um MAPA aba→item (cada aba lembra o próprio item ao trocar de aba
 * sem sair da ficha, F35). A URL só carrega o item da aba ATUAL — o
 * terceiro segmento do caminho, ou `?item=` na esteira —, reconstruído
 * aqui como um mapa de uma entrada só. O que já vive só em memória
 * (trocar de aba e voltar, na mesma sessão) não muda; o que muda é que
 * um F5/link direto não repõe o item das OUTRAS abas.
 */

const ABAS_DA_FICHA: readonly AbaDaFicha[] = [
  "contrato",
  "vigencia",
  "processo",
  "execucao",
  "alteracoes",
  "fiscalizacao",
]

function ehAbaDaFicha(valor: string): valor is AbaDaFicha {
  return (ABAS_DA_FICHA as readonly string[]).includes(valor)
}

const SLUG_DE_TELA: Record<ChaveDeTela, string> = {
  painel: "painel",
  contratos: "contratos",
  avisos: "avisos",
  relatorios: "relatorios",
  fiscalizacao: "fiscalizacao",
  perfil: "perfil",
  configuracoes: "configuracoes",
}

const TELA_DO_SLUG: Record<string, ChaveDeTela> = Object.fromEntries(
  Object.entries(SLUG_DE_TELA).map(([chave, slug]) => [slug, chave as ChaveDeTela]),
)

function submenuDeUmaEntrada(aba: AbaDaFicha, item: string | null): SubmenuDaFicha {
  return item === null ? {} : { [aba]: item }
}

/** A ORIGEM NO ENDEREÇO (spec 0084): `?de=` com o caminho da tela de onde o
 *  contrato foi aberto. Só vale o caminho de uma tela do sistema que não seja
 *  outra ficha nem um processo; o resto é ignorado, e a tela abre sem origem. */
function origemDe(parametros: URLSearchParams): TelaDeOrigem | undefined {
  const de = parametros.get("de")
  if (de === null || !de.startsWith("/")) return undefined
  const tela = caminhoParaTela(...separarBusca(de))
  if (tela === null || tela.nome === "ficha" || tela.nome === "esteira") return undefined
  return tela
}

export function telaParaCaminho(tela: Tela): string {
  if (tela.nome === "ficha") {
    const item = tela.submenu[tela.aba]
    const base = `/ficha/${tela.contratoId}/${tela.aba}`
    const caminho = item === undefined ? base : `${base}/${item}`
    return tela.origem === undefined
      ? caminho
      : `${caminho}?${new URLSearchParams({ de: telaParaCaminho(tela.origem) }).toString()}`
  }
  if (tela.nome === "esteira") {
    const base = `/esteira/${tela.contratoId}/${tela.abaDaFicha}`
    const item = tela.submenuDaFicha[tela.abaDaFicha]
    const parametros = new URLSearchParams()
    if (tela.pecaId !== undefined) parametros.set("peca", String(tela.pecaId))
    if (item !== undefined) parametros.set("item", item)
    if (tela.origem !== undefined) parametros.set("de", telaParaCaminho(tela.origem))
    const busca = parametros.toString()
    return busca === "" ? base : `${base}?${busca}`
  }
  // A aba do Meu perfil no endereço (spec 0079); "Meus dados" é o /perfil puro.
  if (tela.nome === "painel") {
    const busca = buscaDoPainel(tela as EstadoDoPainel)
    return busca === "" ? "/painel" : `/painel?${busca}`
  }
  if (tela.nome === "perfil" && "aba" in tela && tela.aba !== undefined && tela.aba !== "dados") {
    return `/perfil/${tela.aba}`
  }
  if (tela.nome === "configuracoes" && "item" in tela && tela.item !== undefined) {
    if (tela.item === "entidades" && "entidade" in tela && tela.entidade !== undefined) {
      return `/configuracoes/entidades/${tela.entidade}`
    }
    const filtros = new URLSearchParams()
    for (const chave of FILTROS_DO_ITEM[tela.item] ?? []) {
      const valor = chave in tela ? (tela as Record<string, unknown>)[chave] : undefined
      if (typeof valor === "string" && valor !== "") filtros.set(chave, valor)
    }
    const busca = filtros.toString()
    return busca === "" ? `/configuracoes/${tela.item}` : `/configuracoes/${tela.item}?${busca}`
  }
  return `/${SLUG_DE_TELA[tela.nome]}`
}

/** A TELA DE ENTRADA TEM ENDEREÇO PRÓPRIO (pedido de 08/10/2026): sem sessão,
 *  o endereço é `/entrar`, e a tela que a pessoa pediu vai em `?volta=`, para
 *  abrir depois de entrar. O Painel, que é o destino padrão, não precisa dele. */
export function caminhoDaEntrada(tela: Tela): string {
  if (tela.nome === "painel") return "/entrar"
  return `/entrar?${new URLSearchParams({ volta: telaParaCaminho(tela) }).toString()}`
}

function separarBusca(caminho: string): [string, string] {
  const i = caminho.indexOf("?")
  return i < 0 ? [caminho, ""] : [caminho.slice(0, i), caminho.slice(i)]
}

export function caminhoParaTela(pathname: string, busca: string): Tela | null {
  const partes = pathname.split("/").filter((parte) => parte !== "")

  if (partes.length === 0) return { nome: "painel" }

  // `/entrar` abre, depois de entrar, a tela de `?volta=`; um valor de fora do
  // sistema é ignorado, e vale o Painel.
  if (partes.length === 1 && partes[0] === "entrar") {
    const volta = new URLSearchParams(busca).get("volta")
    if (volta === null || !volta.startsWith("/") || volta.startsWith("//")) return { nome: "painel" }
    return caminhoParaTela(...separarBusca(volta)) ?? { nome: "painel" }
  }

  if (partes.length === 1 && partes[0] === "painel") {
    return { nome: "painel", ...estadoDaBusca(busca) }
  }

  if (partes.length === 1) {
    const [unico] = partes
    const tela = unico === undefined ? undefined : TELA_DO_SLUG[unico]
    return tela === undefined ? null : { nome: tela }
  }

  if (partes[0] === "configuracoes" && (partes.length === 2 || partes.length === 3)) {
    const item = partes[1] ?? ""
    if (!ehItemDeConfiguracao(item)) return { nome: "configuracoes" }
    const entidade = Number(partes[2])
    if (item === "entidades" && Number.isInteger(entidade) && entidade > 0) {
      return { nome: "configuracoes", item, entidade }
    }
    const parametros = new URLSearchParams(busca)
    const tela: Record<string, unknown> = { nome: "configuracoes", item }
    if (partes.length === 2) {
      for (const chave of FILTROS_DO_ITEM[item] ?? []) {
        const valor = parametros.get(chave)
        if (valor !== null && valor !== "") tela[chave] = valor
      }
    }
    return tela as Tela
  }

  if (partes[0] === "perfil" && partes.length === 2) {
    const aba = partes[1] ?? ""
    if (aba === "avisos") return { nome: "perfil", aba: "preferencias" }
    return ehAbaDoPerfil(aba) && aba !== "dados" ? { nome: "perfil", aba } : { nome: "perfil" }
  }

  if (partes[0] === "ficha") {
    const [, idTexto, abaTexto, item] = partes
    const contratoId = Number(idTexto)
    if (!Number.isInteger(contratoId) || abaTexto === undefined || !ehAbaDaFicha(abaTexto)) {
      return null
    }
    const origem = origemDe(new URLSearchParams(busca))
    return {
      nome: "ficha",
      contratoId,
      aba: abaTexto,
      submenu: submenuDeUmaEntrada(abaTexto, item ?? null),
      ...(origem === undefined ? {} : { origem }),
    }
  }

  if (partes[0] === "esteira") {
    const [, idTexto, abaTexto] = partes
    const contratoId = Number(idTexto)
    if (!Number.isInteger(contratoId) || abaTexto === undefined || !ehAbaDaFicha(abaTexto)) {
      return null
    }
    const parametros = new URLSearchParams(busca)
    const pecaTexto = parametros.get("peca")
    const pecaId = pecaTexto === null ? undefined : Number(pecaTexto)
    if (pecaTexto !== null && !Number.isInteger(pecaId)) return null
    const origem = origemDe(parametros)
    return {
      nome: "esteira",
      contratoId,
      pecaId,
      abaDaFicha: abaTexto,
      submenuDaFicha: submenuDeUmaEntrada(abaTexto, parametros.get("item")),
      ...(origem === undefined ? {} : { origem }),
    }
  }

  return null
}
